import React, { useState, useMemo, useEffect } from 'react';
import { InfoTooltip } from './InfoTooltip';
import { BreakEvenChart } from './BreakEvenChart';

interface RateBuyDownOptimizerProps {
  loanAmount: number;
  interestRate: number;
  loanTerm: number;
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

const calculatePandI = (loan: number, rate: number, term: number) => {
    if (loan <= 0 || rate <= 0 || term <= 0) return 0;
    const monthlyRate = rate / 100 / 12;
    const totalMonths = term * 12;
    return loan * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);
};

export const RateBuyDownOptimizer: React.FC<RateBuyDownOptimizerProps> = ({ loanAmount, interestRate, loanTerm }) => {
    const [points, setPoints] = useState<number>(1);
    const [rateReduction, setRateReduction] = useState<number>(0.25);
    const [investmentReturn, setInvestmentReturn] = useState<number>(interestRate);
    const [isUserModifiedReturn, setIsUserModifiedReturn] = useState<boolean>(false);
    
    // Refinance Probability State
    const [simulateRefinance, setSimulateRefinance] = useState<boolean>(false);
    const [refinanceYear, setRefinanceYear] = useState<number>(7);

    // Sync investment return with interest rate unless user modified it
    useEffect(() => {
        if (!isUserModifiedReturn) {
            setInvestmentReturn(interestRate);
        }
    }, [interestRate, isUserModifiedReturn]);

    const handleInvestmentReturnChange = (val: number) => {
        setInvestmentReturn(val);
        setIsUserModifiedReturn(true);
    };

    const { costOfPoints, monthlySavings, comparisonData, breakEvenYear } = useMemo(() => {
        const costOfPoints = loanAmount * (points / 100);
        
        const originalPandI = calculatePandI(loanAmount, interestRate, loanTerm);
        const newRate = interestRate - rateReduction;
        const newPandI = calculatePandI(loanAmount, newRate, loanTerm);

        const monthlySavings = originalPandI - newPandI;
        const monthlyInvRate = investmentReturn / 100 / 12;

        // Generate comparison data for 30 years
        const comparisonData = [];
        let breakEvenYear = -1;
        
        // Strategy A (Buy Points): Invest the monthly savings
        // Strategy B (Invest): Invest the costOfPoints upfront
        
        let valueBuyPoints = 0;
        let valueInvest = costOfPoints;

        for (let year = 0; year <= 30; year++) {
             comparisonData.push({
                year,
                strategyBuyPoints: valueBuyPoints,
                strategyInvest: valueInvest
            });

            if (breakEvenYear === -1 && valueBuyPoints > valueInvest) {
                breakEvenYear = year;
            }

            // Advance one year
            // Compounding monthly for precision
            for(let m=0; m<12; m++) {
                valueBuyPoints = (valueBuyPoints * (1 + monthlyInvRate)) + monthlySavings;
                valueInvest = valueInvest * (1 + monthlyInvRate);
            }
        }

        return { costOfPoints, monthlySavings, comparisonData, breakEvenYear };
    }, [loanAmount, interestRate, loanTerm, points, rateReduction, investmentReturn]);

    const getMilestoneDiff = (year: number) => {
        const data = comparisonData[year];
        if(!data) return null;
        const diff = data.strategyBuyPoints - data.strategyInvest;
        return {
            diff,
            isBuyBetter: diff > 0
        };
    };

    // Calculate outcome at Refinance Year
    const refinanceAnalysis = useMemo(() => {
        if (!simulateRefinance) return null;
        
        const dataAtYear = comparisonData[refinanceYear];
        if (!dataAtYear) return null;

        const lostOrGained = dataAtYear.strategyBuyPoints - dataAtYear.strategyInvest;
        return {
            isLoss: lostOrGained < 0,
            amount: Math.abs(lostOrGained)
        };
    }, [comparisonData, simulateRefinance, refinanceYear]);

    const renderMilestoneCard = (year: number, title: string) => {
        const result = getMilestoneDiff(year);
        if (!result) return null;
        
        return (
            <div className={`p-3 rounded-lg border ${result.isBuyBetter ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800' : 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'}`}>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide">{title} (Year {year})</div>
                <div className={`font-bold text-lg ${result.isBuyBetter ? 'text-green-700 dark:text-green-400' : 'text-blue-700 dark:text-blue-400'}`}>
                    {result.isBuyBetter ? 'Higher Value: Buy Points' : 'Higher Value: Invest'}
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                    by <span className="font-bold">{formatCurrency(Math.abs(result.diff))}</span>
                </div>
            </div>
        );
    };

    const inputClass = "w-full p-2 border rounded bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-white transition-colors focus:ring-2 focus:ring-bp-light-blue outline-none";

    return (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 transition-colors">
            <div className="flex items-center mb-4 border-b dark:border-slate-600 pb-2">
                <h3 className="text-xl font-bold text-bp-slate dark:text-white">Rate Buy-Down Optimizer</h3>
                <InfoTooltip text="Should you pay cash upfront to lower your rate? This tool compares two strategies: 1) Paying for points and investing your monthly savings, vs 2) Investing that cash upfront instead." />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Controls */}
                <div className="lg:col-span-1 space-y-6">
                    <div>
                        <label htmlFor="points" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Points to Purchase</label>
                        <input
                            type="number"
                            id="points"
                            value={points}
                            onChange={(e) => setPoints(parseFloat(e.target.value) || 0)}
                            className={inputClass}
                            step="0.125"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Upfront Cost: <strong>{formatCurrency(costOfPoints)}</strong></p>
                    </div>
                    <div>
                        <label htmlFor="rateReduction" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rate Reduction (%)</label>
                        <input
                            type="number"
                            id="rateReduction"
                            value={rateReduction}
                            onChange={(e) => setRateReduction(parseFloat(e.target.value) || 0)}
                            className={inputClass}
                            step="0.125"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Monthly Savings: <strong className="text-green-600 dark:text-green-400">{formatCurrency(monthlySavings)}/mo</strong></p>
                    </div>
                    <div className="pt-4 border-t dark:border-slate-600">
                        <div className="flex items-center mb-1">
                            <label htmlFor="investmentReturn" className="block text-sm font-bold text-gray-700 dark:text-gray-300">Opportunity Cost Rate (%)</label>
                            <InfoTooltip text="What return could you get if you invested this money instead? Defaults to your Mortgage Rate (the guaranteed return of paying off debt)." />
                        </div>
                        <input
                            type="number"
                            id="investmentReturn"
                            value={investmentReturn}
                            onChange={(e) => handleInvestmentReturnChange(parseFloat(e.target.value) || 0)}
                            className="w-full p-2 border rounded bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100 font-semibold outline-none"
                            step="0.1"
                        />
                        {!isUserModifiedReturn && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Auto-set to mortgage rate.</p>
                        )}
                    </div>

                    {/* Future Scenarios Section */}
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                         <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-bold text-yellow-900 dark:text-yellow-100">Future Rate Probability</label>
                            <input 
                                type="checkbox" 
                                checked={simulateRefinance}
                                onChange={(e) => setSimulateRefinance(e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                        </div>
                        <p className="text-xs text-yellow-800 dark:text-yellow-200 mb-3">
                            What if rates drop? Simulate a refinance event.
                        </p>
                        
                        {simulateRefinance && (
                            <div>
                                <label className="block text-xs font-semibold text-yellow-900 dark:text-yellow-100 mb-1">If I Refinance in Year:</label>
                                <input
                                    type="number"
                                    value={refinanceYear}
                                    onChange={(e) => setRefinanceYear(Math.max(1, Math.min(30, parseInt(e.target.value) || 7)))}
                                    className="w-full p-2 text-sm border rounded border-yellow-300 dark:border-yellow-700 bg-white dark:bg-slate-700 dark:text-white outline-none"
                                    min="1"
                                    max="30"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column: Chart and Analysis */}
                <div className="lg:col-span-2">
                    <div className="mb-4 flex items-center justify-between">
                        <div className="flex gap-4 text-sm">
                            <div className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                                <span className="font-medium text-gray-700 dark:text-gray-300">Invest Strategy</span>
                            </div>
                            <div className="flex items-center">
                                <span className="w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                                <span className="font-medium text-gray-700 dark:text-gray-300">Buy Points Strategy</span>
                            </div>
                        </div>
                    </div>

                    <div className="h-64 w-full mb-6 bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-100 dark:border-slate-600 p-2">
                        <BreakEvenChart data={comparisonData} refinanceYear={simulateRefinance ? refinanceYear : null} />
                    </div>
                    
                    {/* Refinance Analysis Box */}
                    {simulateRefinance && refinanceAnalysis && (
                        <div className={`mb-6 p-4 rounded-lg border-l-4 shadow-sm ${refinanceAnalysis.isLoss ? 'bg-red-50 dark:bg-red-900/30 border-red-400' : 'bg-green-50 dark:bg-green-900/30 border-green-400'}`}>
                            <h4 className={`font-bold ${refinanceAnalysis.isLoss ? 'text-red-800 dark:text-red-300' : 'text-green-800 dark:text-green-300'}`}>
                                {refinanceAnalysis.isLoss ? 'Caution: Potential Loss' : 'Safe: You still break even'}
                            </h4>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                                If you buy points today but refinance in <strong>Year {refinanceYear}</strong>, 
                                you would {refinanceAnalysis.isLoss ? 'lose' : 'gain'} approximately 
                                <strong className={refinanceAnalysis.isLoss ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}> {formatCurrency(refinanceAnalysis.amount)} </strong>
                                compared to just investing the cash.
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 italic">
                                Why? When you refinance, you replace your loan. You paid an upfront fee for a lower rate, but didn't keep the loan long enough to save enough monthly payments to cover that fee.
                            </p>
                        </div>
                    )}

                    <h4 className="font-bold text-bp-slate dark:text-white mb-3">Wealth Impact at a Glance</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {renderMilestoneCard(3, "Short Term")}
                        {renderMilestoneCard(7, "Avg Move")}
                        {renderMilestoneCard(15, "Mid Term")}
                        {renderMilestoneCard(30, "Full Term")}
                    </div>

                    <div className="mt-4 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-slate-700 p-3 rounded border border-gray-200 dark:border-slate-600">
                        {breakEvenYear > 0 ? (
                            <p>
                                <strong>Mathematical Projection:</strong> You need to keep the loan for approximately <strong>{breakEvenYear} years</strong> for buying points to result in a higher net worth than simply investing the cash.
                            </p>
                        ) : (
                            <p>
                                <strong>Mathematical Projection:</strong> Buying points is not projected to result in a higher net worth in this scenario. The investment growth on the upfront cash mathematically outperforms the modeled monthly savings over time.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};